import { KeyboardEvent, ReactNode, useState } from "react";
import { Link } from "react-router-dom";
import { Alert, Button, Chip, CircularProgress, LinearProgress, TextField } from "@mui/material";
import { FaArrowLeft, FaPen } from "react-icons/fa";
import {
  AgentTone,
  Answers,
  BUSINESS_TEMPLATES,
  BusinessKey,
  TONES,
  getBusinessTemplate,
  hasSuggestion,
  isAnswersComplete,
  toggleSuggestion,
} from "./agentPromptTemplate";

interface Props {
  businessKey: BusinessKey | null;
  setBusinessKey: (key: BusinessKey) => void;
  answers: Answers;
  setAnswers: (answers: Answers) => void;
  tone: AgentTone;
  setTone: (tone: AgentTone) => void;
  // Промпт написан вручную — предупреждаем, что он будет заменён
  customPromptAgentID: number | null;
  onSave: () => void;
  isSaving: boolean;
}

type Phase = { kind: "business" } | { kind: "question"; index: number } | { kind: "review" };

/**
 * Шаг «Описание»: тип бизнеса → вопросы по одному на экран → сводка ответов.
 * Последний вопрос — тон общения. Из сводки можно вернуться к любому вопросу.
 */
export const AgentDescribeStep = ({
  businessKey,
  setBusinessKey,
  answers,
  setAnswers,
  tone,
  setTone,
  customPromptAgentID,
  onSave,
  isSaving,
}: Props) => {
  // Если ответы уже есть (повторный вход или возврат из проверки) — сразу сводка
  const [phase, setPhase] = useState<Phase>(businessKey ? { kind: "review" } : { kind: "business" });
  // Вопрос открыт из сводки — после ответа возвращаемся в сводку
  const [fromReview, setFromReview] = useState(false);

  const business = businessKey ? getBusinessTemplate(businessKey) : null;
  const fields = business?.fields ?? [];
  // Вопросы + экран выбора тона
  const total = fields.length + 1;

  const chooseBusiness = (key: BusinessKey) => {
    if (key !== businessKey) {
      // Ответы с теми же полями (цены, оплата…) переносим, остальные сбрасываем
      const keys = new Set(getBusinessTemplate(key).fields.map((f) => f.key));
      setAnswers(Object.fromEntries(Object.entries(answers).filter(([k, v]) => keys.has(k) && v.trim())));
      setBusinessKey(key);
    }
    setFromReview(false);
    setPhase({ kind: "question", index: 0 });
  };

  const next = (index: number) => {
    if (fromReview || index + 1 >= total) {
      setFromReview(false);
      setPhase({ kind: "review" });
    } else {
      setPhase({ kind: "question", index: index + 1 });
    }
  };

  const back = (index: number) => {
    if (fromReview) {
      setFromReview(false);
      setPhase({ kind: "review" });
    } else {
      setPhase(index === 0 ? { kind: "business" } : { kind: "question", index: index - 1 });
    }
  };

  const edit = (index: number) => {
    setFromReview(true);
    setPhase({ kind: "question", index });
  };

  const customPromptAlert = customPromptAgentID && (
    <Alert severity="info" className="mb-6">
      У агента уже есть промпт, написанный вручную. Если сохранить ответы здесь, он будет заменён. Чтобы оставить свой
      промпт, проверьте его в{" "}
      <Link to={`/admin/ai-agents/${customPromptAgentID}/prompts`} className="underline">
        «Промтинге»
      </Link>
      .
    </Alert>
  );

  // ---------- Тип бизнеса ----------
  if (phase.kind === "business" || !business) {
    return (
      <Screen>
        {customPromptAlert}
        <h2 className="text-2xl font-bold mb-2">Чем вы занимаетесь?</h2>
        <p className="text-gray-500 mb-6">
          Зададим несколько простых вопросов о вашем бизнесе — агент будет отвечать клиентам на их основе.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {BUSINESS_TEMPLATES.map((b) => {
            const isSelected = b.key === businessKey;
            return (
              <button
                key={b.key}
                type="button"
                onClick={() => chooseBusiness(b.key)}
                className={`rounded-xl border p-3 text-left transition-colors ${
                  isSelected ? "border-[#6b2fa3] bg-[#f3ecfa]" : "border-gray-200 hover:bg-gray-50"
                }`}
              >
                <div className="text-xl mb-1">{b.emoji}</div>
                <div className={`text-sm leading-tight ${isSelected ? "font-semibold text-[#6b2fa3]" : ""}`}>
                  {b.label}
                </div>
              </button>
            );
          })}
        </div>
      </Screen>
    );
  }

  // ---------- Сводка ----------
  if (phase.kind === "review") {
    const isComplete = isAnswersComplete(business.key, answers);
    return (
      <Screen>
        {customPromptAlert}
        <div className="flex items-center justify-between gap-4 mb-2">
          <h2 className="text-2xl font-bold">Проверьте ответы</h2>
          <button
            type="button"
            className="text-sm text-[#6b2fa3] whitespace-nowrap"
            onClick={() => setPhase({ kind: "business" })}
          >
            {business.emoji} {business.label} · изменить
          </button>
        </div>
        <p className="text-gray-500 mb-6">Нажмите на ответ, чтобы изменить его.</p>

        <ul className="border rounded-2xl divide-y mb-8">
          {fields.map((f, index) => {
            const value = answers[f.key]?.trim();
            const isMissing = f.required && !value;
            return (
              <li key={f.key}>
                <ReviewRow label={f.label} onClick={() => edit(index)} missing={isMissing}>
                  {value || (
                    <span className={isMissing ? "text-red-500" : "text-gray-400"}>
                      {isMissing ? "Нужно заполнить" : "Не заполнено"}
                    </span>
                  )}
                </ReviewRow>
              </li>
            );
          })}
          <li>
            <ReviewRow label="Обращение к клиентам" onClick={() => edit(fields.length)}>
              {TONES.find((t) => t.value === tone)?.label}
            </ReviewRow>
          </li>
        </ul>

        <Button
          fullWidth
          variant="contained"
          size="large"
          disabled={!isComplete || isSaving}
          onClick={onSave}
          sx={{ borderRadius: "12px", py: 1.5 }}
        >
          {isSaving ? <CircularProgress size={22} color="inherit" /> : "Сохранить и проверить"}
        </Button>
      </Screen>
    );
  }

  // ---------- Вопрос ----------
  const { index } = phase;
  const isToneQuestion = index >= fields.length;
  const field = fields[index];
  const value = field ? (answers[field.key] ?? "") : "";
  const isOptional = !isToneQuestion && !field.required;
  // «Дальше» — когда есть ответ; необязательный вопрос можно пропустить отдельной кнопкой
  const canGoNext = isToneQuestion || !!value.trim();
  // Обязательные ответы уже есть — остальные вопросы можно пропустить разом
  const canSkipRest = !fromReview && index + 1 < total && isAnswersComplete(business.key, answers);

  const onKeyDown = (e: KeyboardEvent) => {
    // Однострочное поле — Enter, многострочное — Ctrl/Cmd + Enter
    if (e.key === "Enter" && (!field?.multiline || e.ctrlKey || e.metaKey) && (canGoNext || isOptional)) {
      e.preventDefault();
      next(index);
    }
  };

  return (
    <Screen>
      <div className="flex items-center gap-3 mb-6">
        <button
          type="button"
          className="text-gray-500 hover:text-gray-800 p-1"
          onClick={() => back(index)}
          aria-label="Назад"
        >
          <FaArrowLeft />
        </button>
        <div className="flex-grow">
          <LinearProgress
            variant="determinate"
            value={((index + 1) / total) * 100}
            sx={{ height: 6, borderRadius: 3, bgcolor: "#f3ecfa", "& .MuiLinearProgress-bar": { bgcolor: "#6b2fa3" } }}
          />
        </div>
        <div className="text-sm text-gray-500 whitespace-nowrap">
          {index + 1} из {total}
        </div>
      </div>

      <div className="text-sm text-gray-500 mb-1">
        {business.emoji} {business.label}
      </div>

      {isToneQuestion ? (
        <>
          <h2 className="text-2xl font-bold mb-6">Как агенту обращаться к клиентам?</h2>
          <div className="grid grid-cols-2 gap-3 mb-8">
            {TONES.map((t) => (
              <button
                key={t.value}
                type="button"
                onClick={() => {
                  setTone(t.value);
                  next(index);
                }}
                className={`rounded-xl border p-4 text-left transition-colors ${
                  tone === t.value ? "border-[#6b2fa3] bg-[#f3ecfa]" : "border-gray-200 hover:bg-gray-50"
                }`}
              >
                <div className="font-semibold">{t.label}</div>
                <div className="text-sm text-gray-500">{t.hint}</div>
              </button>
            ))}
          </div>
        </>
      ) : (
        <>
          <h2 className="text-2xl font-bold mb-1">{field.question}</h2>
          <div className="text-sm text-gray-400 mb-4">
            {field.required ? "Обязательный вопрос" : "Можно пропустить"}
          </div>
          <TextField
            key={field.key}
            autoFocus
            fullWidth
            multiline={field.multiline}
            minRows={field.multiline ? 3 : undefined}
            maxRows={field.multiline ? 10 : undefined}
            value={value}
            onChange={(e) => setAnswers({ ...answers, [field.key]: e.target.value })}
            onKeyDown={onKeyDown}
            placeholder={`Например: ${field.placeholder}`}
            helperText={field.hint ?? (field.multiline ? "Ctrl + Enter — дальше" : "Enter — дальше")}
          />
          {field.suggestions && (
            <div className="flex flex-wrap gap-2 mt-3">
              {field.suggestions.map((suggestion) => {
                const isSelected = hasSuggestion(value, suggestion);
                return (
                  <Chip
                    key={suggestion}
                    label={suggestion}
                    clickable
                    variant={isSelected ? "filled" : "outlined"}
                    onClick={() => setAnswers({ ...answers, [field.key]: toggleSuggestion(value, suggestion) })}
                    sx={isSelected ? { bgcolor: "#f3ecfa", color: "#6b2fa3", borderColor: "#6b2fa3" } : undefined}
                  />
                );
              })}
            </div>
          )}
          <div className="mb-8" />
        </>
      )}

      <div className="flex gap-3">
        <Button variant="outlined" size="large" onClick={() => back(index)} sx={{ borderRadius: "12px" }}>
          Назад
        </Button>
        <Button
          fullWidth
          variant="contained"
          size="large"
          disabled={!canGoNext}
          onClick={() => next(index)}
          sx={{ borderRadius: "12px" }}
        >
          {fromReview ? "Готово" : "Дальше"}
        </Button>
      </div>

      {(isOptional || canSkipRest) && (
        <div className="flex flex-wrap justify-center items-center gap-x-3 gap-y-1 mt-4 text-sm text-gray-500">
          {isOptional && (
            <button type="button" className="hover:text-[#6b2fa3] underline" onClick={() => next(index)}>
              Пропустить
            </button>
          )}
          {isOptional && canSkipRest && <span className="text-gray-300">·</span>}
          {canSkipRest && (
            <button
              type="button"
              className="hover:text-[#6b2fa3] underline"
              onClick={() => setPhase({ kind: "review" })}
            >
              Пропустить остальные вопросы и перейти к сводке
            </button>
          )}
        </div>
      )}
    </Screen>
  );
};

const Screen = ({ children }: { children: ReactNode }) => (
  <div className="h-full overflow-y-auto">
    <div className="max-w-2xl mx-auto px-4 sm:px-8 py-8">{children}</div>
  </div>
);

const ReviewRow = ({
  label,
  onClick,
  missing,
  children,
}: {
  label: string;
  onClick: () => void;
  missing?: boolean;
  children: ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-gray-50"
  >
    <div className="flex-grow min-w-0">
      <div className={`text-sm ${missing ? "text-red-500" : "text-gray-500"}`}>{label}</div>
      <div className="whitespace-pre-wrap break-words">{children}</div>
    </div>
    <FaPen className="text-gray-300 shrink-0 mt-1" size={12} />
  </button>
);

export default AgentDescribeStep;
